import React, { useEffect, useRef, useState } from 'react';
import { Input } from './input';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  ADDRESS_PRIMARY_TYPES,
  toAddressSelection,
  toPlaceTypes,
  type AddressSelection,
  type PlaceType,
} from '@/lib/placeSuggestions';

export type { PlaceType };

declare global {
  interface Window {
    google: any; // Use any type to avoid conflicts with other declarations
  }
}

// The subset of the Places (New) library this component uses. Kept structural because the
// ambient Window.google is untyped.
type LoadedPlace = {
  fetchFields(options: { fields: string[] }): Promise<unknown>;
} & Parameters<typeof toAddressSelection>[0];

interface PlacesLibrary {
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions(request: Record<string, unknown>): Promise<{ suggestions: any[] }>;
  };
  AutocompleteSessionToken: new () => unknown;
  Place: new (options: { id: string }) => LoadedPlace;
}

interface GooglePlacesAutocompleteProps {
  onAddressSelect: (address: AddressSelection) => void;
  placeholder?: string;
  value?: string;
  required?: boolean;
  error?: string;
  id?: string;
  name?: string;
  onChange?: (value: string) => void;
  className?: string;
  autocomplete?: string;
  dropdownPosition?: 'above' | 'below' | 'auto';
}

export function GooglePlacesAutocomplete({
  onAddressSelect,
  placeholder = 'Search for an address',
  value = '',
  required = false,
  error,
  id,
  name,
  onChange,
  className = '',
  autocomplete,
  dropdownPosition = 'below',
}: GooglePlacesAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<PlaceType[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isGoogleMapsReady, setIsGoogleMapsReady] = useState(false);
  const places = useRef<PlacesLibrary | null>(null);
  // One token spans the keystrokes and the details fetch that ends them, so Google bills the
  // whole search as a single session.
  const sessionToken = useRef<unknown>(null);
  // Suggestions resolve out of order; only the latest keystroke's may land.
  const latestRequest = useRef(0);
  // toPlace() on the prediction carries the session token into the details fetch.
  const predictions = useRef(new Map<string, { toPlace: () => LoadedPlace }>());
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    let cancelled = false;
    let checkGoogleInterval: ReturnType<typeof setInterval> | null = null;
    let retryCount = 0;
    const maxRetries = 25;

    const loadPlaces = async () => {
      try {
        const library = (await window.google.maps.importLibrary('places')) as PlacesLibrary;
        if (cancelled) return;
        places.current = library;
        setIsGoogleMapsReady(true);
      } catch (err) {
        console.error('Google Places library failed to load. Autocomplete will not work.', err);
      }
    };

    // importLibrary appears once the bootstrap script has run, whichever component injected it.
    const whenBootstrapped = () => {
      if (window.google?.maps?.importLibrary) {
        void loadPlaces();
        return;
      }
      checkGoogleInterval = setInterval(() => {
        retryCount++;
        if (window.google?.maps?.importLibrary) {
          if (checkGoogleInterval) clearInterval(checkGoogleInterval);
          void loadPlaces();
        } else if (retryCount >= maxRetries) {
          if (checkGoogleInterval) clearInterval(checkGoogleInterval);
          console.error('Google Maps API failed to load after multiple retries. Autocomplete will not work.');
        }
      }, 200);
    };

    if (!window.google?.maps?.importLibrary &&
        !document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      script.id = 'google-maps-script';
      script.onerror = () => console.error('Failed to load Google Maps script');
      document.head.appendChild(script);
    }
    whenBootstrapped();

    return () => {
      cancelled = true;
      if (checkGoogleInterval) clearInterval(checkGoogleInterval);
    };
  }, []);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const clearSuggestions = () => {
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const fetchSuggestions = async (input: string) => {
    const library = places.current;
    if (!library) {
      clearSuggestions();
      return;
    }
    const request = ++latestRequest.current;
    sessionToken.current ??= new library.AutocompleteSessionToken();

    try {
      const { suggestions: results } = await library.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedPrimaryTypes: ADDRESS_PRIMARY_TYPES,
        includedRegionCodes: ['us'],
        sessionToken: sessionToken.current,
      });
      if (request !== latestRequest.current) return;
      predictions.current = new Map(
        results.flatMap(r => (r.placePrediction ? [[r.placePrediction.placeId, r.placePrediction]] : []))
      );
      const rows = toPlaceTypes(results);
      setSuggestions(rows);
      setShowSuggestions(rows.length > 0);
    } catch (err) {
      if (request !== latestRequest.current) return;
      console.warn('Error getting place suggestions:', err);
      clearSuggestions();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setHighlightedIndex(-1);

    if (value.length > 2) {
      void fetchSuggestions(value);
    } else {
      latestRequest.current++;
      clearSuggestions();
    }

    onChange?.(value);
  };

  const handleSuggestionClick = async (placeId: string, description: string) => {
    setInputValue(description);
    setShowSuggestions(false);
    latestRequest.current++;

    const library = places.current;
    if (!library) {
      console.warn('Google Places library not available');
      return;
    }

    try {
      const place = predictions.current.get(placeId)?.toPlace() ?? new library.Place({ id: placeId });
      await place.fetchFields({ fields: ['addressComponents', 'formattedAddress', 'location'] });
      onAddressSelect(toAddressSelection(place));
    } catch (err) {
      console.error('Failed to load address details for place ID:', placeId, err);
    } finally {
      sessionToken.current = null;
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    // Arrow down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    }
    
    // Arrow up
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
    }
    
    // Enter
    if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      const suggestion = suggestions[highlightedIndex];
      void handleSuggestionClick(suggestion.place_id, suggestion.description);
    }
    
    // Escape
    if (e.key === 'Escape') {
      e.preventDefault();
      setShowSuggestions(false);
    }
  };

  return (
    <div className="relative w-full">
      <div className="flex h-10 w-full items-center justify-between rounded-md border border-border bg-background px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px] focus-within:border-primary disabled:cursor-not-allowed disabled:opacity-50">
        <Input
          ref={inputRef}
          type="text"
          placeholder={isGoogleMapsReady ? placeholder : "Loading address search..."}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-full border-0 bg-transparent p-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground",
            error && 'text-destructive',
            className
          )}
          required={required}
          id={id}
          name={name}
          autoComplete={autocomplete}
        />
        {inputValue && (
          <button
            type="button"
            onClick={() => {
              setInputValue('');
              setSuggestions([]);
              setShowSuggestions(false);
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }}
            className="ml-2 h-4 w-4 opacity-70 hover:opacity-100 flex-shrink-0"
            aria-label="Clear input"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className={cn(
            "absolute z-[9999] w-full overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg",
            dropdownPosition === 'above' ? "bottom-full mb-1" : "top-full mt-1",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          )}
        >
          <div 
            className="p-1 overflow-y-auto max-h-60"
            onWheel={(e) => {
              // Allow wheel scrolling within this container
              e.stopPropagation();
            }}
          >
            {suggestions.length > 8 && (
              <div className="flex cursor-default items-center justify-center py-1">
                <ChevronUp className="h-4 w-4 opacity-50" />
              </div>
            )}
            
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.place_id}
                className={cn(
                  "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none",
                  index === highlightedIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground"
                )}
                onClick={() => void handleSuggestionClick(suggestion.place_id, suggestion.description)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex flex-col">
                  <div className="font-medium">{suggestion.structured_formatting.main_text}</div>
                  <div className="text-xs text-muted-foreground">{suggestion.structured_formatting.secondary_text}</div>
                </div>
              </div>
            ))}
            
            {suggestions.length > 8 && (
              <div className="flex cursor-default items-center justify-center py-1">
                <ChevronDown className="h-4 w-4 opacity-50" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
